import React, { useState, useEffect, Suspense } from "react";
import Localisation from "./Localisation";
import GraphModal from "./GraphModal";
import Rows from "../../components/Rows";
import PageScroller from "../../components/PageScroller";
import { formatNumber } from "../../utils/format";

const BarChart = React.lazy(() =>
  import("react-chartjs-2").then((module) => ({ default: module.Bar }))
);

const DashboardContent = ({ graphs, dashboardId, filters }) => {
  const sortedGraphs = graphs.sort((a, b) => a.order - b.order);
  const [selectedGraph, setSelectedGraph] = useState(null);
  const [chartRegistered, setChartRegistered] = useState(false); 


  useEffect(() => {
    let isMounted = true;
    const registerChart = async () => {
      const { Chart, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } =
        await import("chart.js");
      Chart.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);
      if (isMounted) setChartRegistered(true);
    };
    registerChart();
    return () => (isMounted = false); 
  }, []);

  const openModal = (graph) => setSelectedGraph(graph);
  const closeModal = () => setSelectedGraph(null);

  const renderGraph = (graph) => {
    switch (graph.type) {
      case "KPI":
        return renderKpi(graph);
      case "BAR_CHART":
        return renderBarChart(graph, chartRegistered); 
      case "MAP_CHART":
        return <Localisation data={{ graphs: [graph] }} />;
      case "TABLE":
        return <TableGraph graph={graph} />;
      default:
        return <p className="text-gray-500">Unsupported Graph Type</p>;
    }
  };

  return (
    <div className="grid grid-cols-12 gap-4"
      style={{
        gridAutoRows: "minmax(100px, auto)",
      }}
    >
      {sortedGraphs.map((graph) => (
        <div
          key={graph.id}
          className={`col-span-${graph.columns} row-span-${graph.rows} `}
          style={{
            gridColumnEnd: `span ${graph.columns}`,
            gridRowEnd: `span ${graph.rows}`,
          }}
          onClick={() => openModal(graph)}
        >
          {renderGraph(graph)}
        </div>
      ))}
      {selectedGraph && (
        <GraphModal
          graph={selectedGraph}
          dashboardId={dashboardId}
          onClose={closeModal}
          filters={filters}
        />
      )}
    </div>
  );
};



const renderKpi = (graph) => {
  const { title, results, configuration } = graph;
  const textColor = configuration?.kpi?.textColor || "#000";
  const unit = configuration?.kpi?.unit || "";
  const value = results?.[0]?.kpi ?? "";

  return (
    <div
      className="flex flex-col items-start h-full bg-white p-4 shadow-[0px_6px_26px_rgba(98,107,125,0.07)]"
      style={{
        border: `1.5pt solid ${textColor}`,
        boxShadow: "0pt 6pt 26pt #626B7D12",
        borderRadius: "12pt",
        opacity: 1,
      }}
    >
      <h3 className="text-[#707070] text-[16px] font-semibold">{title}</h3>
      <p className="text-2xl font-bold" style={{ color: textColor }}>
        {formatNumber(value)} {unit}
      </p>
    </div>
  );
};

const renderBarChart = (graph, chartRegistered) => {
  if (!chartRegistered) return <div></div>;

  const { title, configuration, results = [] } = graph;
  const barColor = configuration?.barChart?.color || "#FEA26F";

  const labels = results.map((item) => item.x);
  const dataValues = results.map((item) => item.y);

  const data = {
    labels,
    datasets: [
      {
        label: title,
        data: dataValues,
        backgroundColor: barColor,
        borderRadius: 10,
        barPercentage: 0.6,
        categoryPercentage: 0.9,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        titleColor: "#FFF",
        bodyColor: "#FFF",
        borderWidth: 1,
        borderColor: "#FFFFFF",
        padding: 10,
        displayColors: false,
        mode: "index",
        intersect: false,
        callbacks: {
          label: function (tooltipItem) {
            return `${tooltipItem.raw}`;
          },
          title: function () {
            return "";
          },
        },
      },
    },
    layout: { padding: 0 },
    scales: {
      x: {
        ticks: {
          color: "#38485C",
          font: {
            size: 12,
            family: "Montserrat",
            weight: 600,
          },
        },
        grid: { display: false },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: "#38485C",
          font: {
            size: 12,
            family: "Montserrat",
            weight: 600,
          },
        },
        grid: { display: false },
        border: { display: false },
      },
    },
  };



  return (
    <div className="h-full w-full bg-white p-4 rounded-[12pt] shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[#919191] text-[16px] font-semibold">{title}</h3>
      </div>
      <div className="h-[300px] w-full">
        <Suspense fallback={<div></div>}>
          <BarChart data={data} options={options} />
        </Suspense>     </div>
    </div>
  );
};

const TableGraph = ({ graph }) => {
  const { configuration, results = [] } = graph;
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const columns = configuration?.table?.keys.map((key) => ({
    key: key.name,
    label: key.name,
  }));

  return (
    <div>
      <Rows columns={columns} data={results.slice(pageNumber * pageSize, (pageNumber + 1) * pageSize)} />
      <PageScroller
        pageNumber={pageNumber}
        totalPages={Math.ceil(results.length / pageSize)}
        pageSize={pageSize}
        onPageChange={setPageNumber}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
};




export default DashboardContent;
